import { toast } from '@/components/ui/toaster'
import type { DataAsset } from '@/hooks/api/use-data-assets-query'
import {
  DELETE_DATA_ASSET_ERROR,
  useDeleteDataAssetsMutation,
} from '@/hooks/api/use-delete-asset-mutation'
import { useTranslate } from '@tolgee/react'
import { useCallback, useState } from 'react'

interface UseDataAssetDeleteProps {
  dataAssets: DataAsset[]
  selectedIds: string[]
  selectedCount: number
  clearSelection: () => void
}

export function useDataAssetDelete({
  dataAssets,
  selectedIds,
  selectedCount,
  clearSelection,
}: UseDataAssetDeleteProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const { t } = useTranslate()
  const deleteDataAssetsMutation = useDeleteDataAssetsMutation()

  // Helper function to clean up column enrichment workflows for deleted DATA assets
  const cleanupColumnEnrichmentWorkflows = useCallback((deletedAssets: DataAsset[]) => {
    deletedAssets.forEach((asset) => {
      if (asset.dataType === 'DATA' && asset.schemaName && asset.tableNames?.[0]) {
        const schemaName = asset.schemaName
        const tableName = asset.tableNames[0]
        const storageKey = `column_enrich_${schemaName}_${tableName}`

        try {
          // Check if the workflow exists in localStorage
          const existingWorkflow = localStorage.getItem(storageKey)
          if (existingWorkflow) {
            // Remove the workflow from localStorage
            localStorage.removeItem(storageKey)
          }
        } catch (error) {
          console.error(`Failed to cleanup workflow for ${schemaName}.${tableName}:`, error)
        }
      }
    })
  }, [])

  const handleDeleteSelected = useCallback(() => {
    if (selectedCount === 0) return
    setIsDeleteDialogOpen(true)
  }, [selectedCount])

  const handleConfirmDelete = useCallback(async () => {
    if (selectedCount === 0) return

    try {
      const deleteRequests = selectedIds.map((assetId) => {
        const asset = dataAssets.find((a: DataAsset) => a.id === assetId)
        if (!asset) throw new Error(`Asset with id ${assetId} not found`)

        return {
          dataAssetFqdn: asset.id,
          dataAssetType: asset.dataType,
        }
      })

      // Get the assets that will be deleted for cleanup
      const assetsToDelete = selectedIds
        .map((assetId) => dataAssets.find((a: DataAsset) => a.id === assetId))
        .filter((asset): asset is DataAsset => asset !== undefined)

      await deleteDataAssetsMutation.mutateAsync({ deleteRequests })

      // Clean up column enrichment workflows for deleted DATA assets
      cleanupColumnEnrichmentWorkflows(assetsToDelete)

      const successMessage =
        selectedCount === 1
          ? t('dataCatalog.delete.toast.success.single')
          : t('dataCatalog.delete.toast.success.multiple', { count: selectedCount })

      toast.success(successMessage)
      clearSelection()
      setIsDeleteDialogOpen(false)
    } catch (error) {
      console.error('Delete failed:', error)

      let errorMessage = t('dataCatalog.optionsMenu.delete.toast.error')
      if (error instanceof Error && error.message && error.message !== DELETE_DATA_ASSET_ERROR) {
        errorMessage = error.message
      }

      toast.error(errorMessage)
    } finally {
      clearSelection()
      setIsDeleteDialogOpen(false)
    }
  }, [
    selectedIds,
    selectedCount,
    clearSelection,
    dataAssets,
    deleteDataAssetsMutation,
    cleanupColumnEnrichmentWorkflows,
    t,
  ])

  return {
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleDeleteSelected,
    handleConfirmDelete,
    isLoading: deleteDataAssetsMutation.isPending,
  }
}
